// FMOD's Emscripten module owns its own memory and audio context, separate from Rust/WASM.
let runtime;
let pending;
let failure;
let gestureInstalled = false;
let userActivated = false;

function check(result, operation) {
    if (result !== 0) throw new Error(`${operation}: FMOD result ${result}`);
}
function vector(v) { return { x: v[0], y: v[1], z: v[2] }; }
function attributes(json) {
    const a = JSON.parse(json);
    return { position: vector(a.position), velocity: vector(a.velocity), forward: vector(a.forward), up: vector(a.up) };
}
async function load() {
    const config = globalThis.AETHER_FMOD ?? {};
    const url = new URL(config.scriptUrl ?? '/fmodstudio.js', globalThis.location.href);
    if (!globalThis.FMODModule) {
        await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url.href;
            script.onload = resolve;
            script.onerror = () => reject(new Error(`Could not load ${url}; install the FMOD HTML5 SDK runtime`));
            document.head.appendChild(script);
        });
    }
    const module = await globalThis.FMODModule({
        locateFile: path => path.endsWith('.wasm')
            ? new URL(config.wasmUrl ?? 'fmodstudio.wasm', url).href : new URL(path, url).href,
    });
    const out = {};
    check(module.Studio_System_Create(out), 'Studio_System_Create');
    const studio = out.val;
    try {
        check(studio.getCoreSystem(out), 'getCoreSystem');
        const core = out.val;
        check(studio.initialize(512, module.STUDIO_INIT_SYNCHRONOUS_UPDATE, module.INIT_3D_RIGHTHANDED, null), 'initialize');
        runtime = { module, studio, core, voices: new Map(), banks: new Map(), samples: new Map(), files: new Set(), next: 0 };
        if (userActivated) check(core.mixerResume(), 'mixerResume');
    } catch (error) { studio.release(); throw error; }
}
export function fmod_ready() {
    if (failure) throw failure;
    if (!gestureInstalled) {
        gestureInstalled = true;
        const activate = () => {
            userActivated = true;
            if (runtime) {
                try { check(runtime.core.mixerResume(), 'mixerResume'); } catch (error) { failure = error; }
            }
        };
        document.addEventListener('pointerdown', activate, { passive: true });
        document.addEventListener('keydown', activate, { passive: true });
    }
    if (!userActivated && !globalThis.navigator?.userActivation?.hasBeenActive) return false;
    userActivated = true;
    if (!pending) pending = load().catch(error => { failure = error; });
    return !!runtime;
}
export function fmod_resource(id, bytes) {
    if (runtime.files.has(id)) return;
    runtime.module.FS_createDataFile('/', `aether-${id}`, bytes, true, false);
    runtime.files.add(id);
}
export function fmod_start(json, pose, volume) {
    const source = JSON.parse(json).source;
    const { module, studio, core } = runtime;
    const out = {};
    let voice;
    if (source.Studio) {
        for (const bank of source.Studio.banks) {
            if (runtime.banks.has(bank)) continue;
            check(studio.loadBankFile(`/aether-${bank}`, module.STUDIO_LOAD_BANK_NORMAL, out), 'loadBankFile');
            runtime.banks.set(bank, out.val);
        }
        check(studio.getEvent(source.Studio.event, out), 'getEvent');
        check(out.val.createInstance(out), 'createInstance');
        voice = { studio: out.val };
    } else {
        const sample = source.Sample;
        let sound = runtime.samples.get(sample.audio);
        if (!sound) {
            check(core.createSound(`/aether-${sample.audio}`, module._3D, null, out), 'createSound');
            sound = out.val;
            runtime.samples.set(sample.audio, sound);
        }
        check(core.playSound(sound, null, true, out), 'playSound');
        voice = { channel: out.val };
        check(voice.channel.setMode(module._3D | (sample.looping ? module.LOOP_NORMAL : module.LOOP_OFF)), 'setMode');
        check(voice.channel.set3DMinMaxDistance(sample.min_distance, sample.max_distance), 'set3DMinMaxDistance');
    }
    const id = ++runtime.next;
    runtime.voices.set(id, voice);
    try {
        fmod_voice(id, pose, volume);
        check(voice.studio ? voice.studio.start() : voice.channel.setPaused(false), 'start');
        return id;
    } catch (error) { fmod_stop(id, false); throw error; }
}
export function fmod_voice(id, pose, volume) {
    const voice = runtime.voices.get(id);
    const a = attributes(pose);
    if (voice.studio) {
        check(voice.studio.set3DAttributes(a), 'set3DAttributes');
        check(voice.studio.setVolume(volume), 'setVolume');
    } else {
        check(voice.channel.set3DAttributes(a.position, a.velocity), 'set3DAttributes');
        check(voice.channel.setVolume(volume), 'setVolume');
    }
}
export function fmod_playing(id) {
    const voice = runtime.voices.get(id);
    const out = {};
    if (voice.studio) {
        check(voice.studio.getPlaybackState(out), 'getPlaybackState');
        return out.val !== runtime.module.STUDIO_PLAYBACK_STOPPED;
    }
    return voice.channel.isPlaying(out) === 0 && !!out.val;
}
export function fmod_stop(id, fadeout) {
    const voice = runtime.voices.get(id);
    if (!voice) return;
    runtime.voices.delete(id);
    if (voice.studio) {
        check(voice.studio.stop(fadeout ? runtime.module.STUDIO_STOP_ALLOWFADEOUT : runtime.module.STUDIO_STOP_IMMEDIATE), 'stop');
        check(voice.studio.release(), 'release');
    } else {
        voice.channel.stop(); // Finished/stolen Core channels can already be invalid.
    }
}
export function fmod_update(listener) {
    check(runtime.studio.setListenerAttributes(0, attributes(listener), null), 'setListenerAttributes');
    check(runtime.studio.update(), 'update');
}
export function fmod_release() {
    if (runtime) {
        for (const id of runtime.voices.keys()) fmod_stop(id, false);
        runtime.studio.release();
        for (const id of runtime.files) runtime.module.FS_unlink(`/aether-${id}`);
    }
    runtime = undefined;
    pending = undefined;
}
